import type { ScholarshipPlayer } from '../../../../types/scholarships';
import type { RosterSport } from '../../../../constants/sport';
import { formatPitchShapes, playsPitcher, sportHasArmAngle } from './rosterPalette';

/**
 * The scouting facts a Players-tab row carries under the name, as short parts joined by " · ".
 * Kept as lines under the name rather than columns: the table already scrolls sideways on the
 * money columns, and a coach reads these as one description of the player, not as values to sort.
 * Parts with nothing in them are left out, so a bare row adds no line at all.
 */
export const profileParts = (player: ScholarshipPlayer): string[] => {
  const size = [player.height, player.weight != null ? `${player.weight} lb` : null]
    .filter(Boolean)
    .join(', ');
  // Status carries its label: type and status share vocabulary ("Returner" is both), and two
  // bare words side by side read as a typo.
  return [
    size,
    player.playerType,
    player.playerStatus ? `Status: ${player.playerStatus}` : null,
    player.committedSchool ? `Committed: ${player.committedSchool}` : null,
    player.school ? `School: ${player.school}` : null,
    player.travelTeam ? `Travel: ${player.travelTeam}` : null,
  ].filter((part): part is string => Boolean(part));
};

/**
 * Velo, arm angle and pitch shapes, only for a player who pitches (either position). Softball
 * leaves arm angle out, even when a record still carries one from before the board was softball.
 */
export const pitchingParts = (player: ScholarshipPlayer, sport: RosterSport): string[] => {
  if (!playsPitcher(player)) return [];
  return [
    player.velo ? `Velo ${player.velo}` : null,
    sportHasArmAngle(sport) ? player.armAngle : null,
    formatPitchShapes(player.pitchShapes) || null,
  ].filter((part): part is string => Boolean(part));
};
